import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { Menu, X, Bell, User, LogOut, Settings } from 'lucide-react'
import { useTheme } from 'next-themes'
import ThemeToggle from '../../../components/ThemeToggle.jsx'
import useAsset from '../../../hooks/useAsset'

const SecretHeader: React.FC = () => {
  const { user, secretUser, signOut, isAdmin } = useAuth()
  const { unreadCount } = useNotifications()
  const { theme } = useTheme()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [currentTheme, setCurrentTheme] = useState('light')
  const navigate = useNavigate()
  const location = useLocation()

  // Cache-busted logo assets
  const logoWhite = useAsset('/tailtations-white.svg')
  const logoDark = useAsset('/tailtations.svg')

  console.log('[SecretHeader] Render - signOut ref:', signOut, 'isAdmin:', isAdmin, 'user:', user?.id)

  // Update theme when it changes
  useEffect(() => {
    const updateTheme = () => {
      const isDark = document.documentElement.classList.contains('dark')
      setCurrentTheme(isDark ? 'dark' : 'light')
    }

    // Initial theme detection
    updateTheme()

    // Watch for theme changes
    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    return () => observer.disconnect()
  }, [theme])

  // Close profile menu when clicking outside
  useEffect(() => {
    if (!profileMenuOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      // Check if click is outside the profile menu
      if (!target.closest('[data-profile-menu]')) {
        setProfileMenuOpen(false)
      }
    }

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true)
    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [profileMenuOpen])

  const handleSignOut = async () => {
    console.log('[SecretHeader] handleSignOut called')
    try {
      console.log('[SecretHeader] Calling signOut...')
      await signOut()
      console.log('[SecretHeader] signOut completed, navigating...')
      // Small delay to ensure auth state propagates before navigation
      setTimeout(() => navigate('/'), 100)
    } catch (error) {
      console.error('[SecretHeader] Sign out error:', error)
      // Navigate anyway in case of error
      navigate('/')
    }
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <header className="bg-background border-b border-border sticky top-0 z-50 pt-safe">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-[100px] md:h-[150px]">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2">
        <img
          src={currentTheme === 'dark' ? logoWhite : logoDark}
          alt="Tailtations"
          className="h-20 w-auto"
        />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            <Link
              to="/dashboard"
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/dashboard')
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Dashboard
            </Link>
            <Link
              to="/events"
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/events')
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Events
            </Link>
            <Link
              to="/orders"
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                isActive('/orders')
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              My Orders
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/admin')
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Admin
              </Link>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Notifications */}
            <button
              onClick={() => navigate('/notifications')}
              className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Profile Menu */}
            <div className="relative" data-profile-menu>
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center space-x-2 p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  {secretUser?.first_name ? (
                    <span className="text-white text-sm font-medium">
                      {secretUser.first_name.charAt(0).toUpperCase()}
                    </span>
                  ) : (
                    <User size={16} className="text-white" />
                  )}
                </div>
              </button>

              {/* Profile Dropdown */}
              {profileMenuOpen && (
                <div className="absolute right-0 mt-1 w-56 rounded-lg shadow-sm border border-border z-[60] overflow-hidden" data-profile-menu>
                  {/* Background overlay to cover header border */}
                  <div className="absolute inset-0 bg-background -top-2"></div>
                  <div className="relative bg-background rounded-lg">
                  <div className="p-4 border-b border-border">
                    <p className="text-sm font-medium text-foreground">
                      {secretUser?.first_name} {secretUser?.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {secretUser?.email || secretUser?.phone}
                    </p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => {
                        navigate('/profile')
                        setProfileMenuOpen(false)
                      }}
                      className="flex items-center space-x-2 w-full p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                    >
                      <Settings size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={(e) => {
                        console.log('[SecretHeader] Sign Out button clicked', e)
                        handleSignOut()
                      }}
                      className="flex items-center space-x-2 w-full p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md"
                    >
                      <LogOut size={16} />
                      <span>Sign Out</span>
                    </button>
                  </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="space-y-2">
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/dashboard')
                    ? 'text-primary bg-muted'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/events"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/events')
                    ? 'text-primary bg-muted'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Events
              </Link>
              <Link
                to="/orders"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-sm font-medium transition-colors ${
                  isActive('/orders')
                    ? 'text-primary bg-muted'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                My Orders
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'text-primary bg-muted'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Admin
                </Link>
              )}
              <button
                onClick={() => {
                  setMobileMenuOpen(false)
                  handleSignOut()
                }}
                className="flex items-center space-x-2 w-full px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default SecretHeader